import {
  Box,
  ButtonBase,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Fragment, PropsWithChildren, useMemo } from 'react'

import {
  ArtistPreSaleLotteryApplicantsConnectionFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { TableScrollFetcher } from '@/components/organisms/functionals'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type LotteryApplicantTableCellProps = {
  loading: boolean
  hasNextPage: boolean
  isEditable?: boolean
  selectedWinners?: string[]
  lotteryApplicants: Maybe<ArtistPreSaleLotteryApplicantsConnectionFragment>
  onSelectedWinner?: (id: string, userId: string) => void
  onFetchMore: () => Promise<void>
}

export const LotteryApplicantTableCell: Cell<LotteryApplicantTableCellProps> =
  ({
    loading,
    hasNextPage,
    isEditable,
    selectedWinners,
    lotteryApplicants,
    onSelectedWinner,
    onFetchMore,
  }) => {
    const { t } = useTranslation('consoleLotteryManagements')

    return (
      <CellFrame>
        <TableContainer>
          <Table
            sx={{
              width: 'auto',
              overflowX: 'scroll',
              mt: 3,
              ...styleSets.hideScrollbar(),
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 120, py: 2, px: 0 }}>
                  <Typography variant="subtitle1" color="text.hint">
                    {t('fullName')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 70, py: 2, px: 0 }}>
                  <Typography variant="subtitle1" color="text.hint">
                    {t('status')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 100, py: 2, px: 0 }}>
                  <Typography variant="subtitle1" color="text.hint">
                    {t('choice')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 150, py: 2, px: 0 }}>
                  <Typography variant="subtitle1" color="text.hint">
                    {t('title')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 80, py: 2, px: 0 }}>
                  <Typography variant="subtitle1" color="text.hint">
                    {t('unitName')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 80, py: 2, px: 0 }}>
                  <Typography variant="subtitle1" color="text.hint">
                    {t('purchaseCountChoice')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 130, py: 2, px: 0 }}>
                  <Typography variant="subtitle1" color="text.hint">
                    {t('paymentMethod')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 150, py: 2, px: 0 }}>
                  <Typography variant="subtitle1" color="text.hint">
                    {t('appliedAt')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableScrollFetcher
              loading={loading}
              hasNextPage={hasNextPage}
              onFetchMore={onFetchMore}
              component={TableRow}
              colSpan={8}
              nodes={lotteryApplicants?.edges?.map(({ node }) => (
                <Fragment key={node.id}>
                  <TableRowCell
                    width={120}
                    preSaleId={node.preSale.id}
                    userId={node.user.id}
                    isEditable={isEditable}
                  >
                    <Typography
                      variant="body2"
                      sx={{ width: 120, pl: 2, ...styleSets.lineCamp(1) }}
                    >
                      {node.user.fullName}
                    </Typography>
                  </TableRowCell>
                  <TableRowCell
                    width={70}
                    preSaleId={node.preSale.id}
                    userId={node.user.id}
                    isEditable={isEditable}
                  >
                    <Typography
                      variant="body2"
                      sx={{ width: 70, ...styleSets.lineCamp(1) }}
                    >
                      <LotteryStatusTag status={node.status} />
                      {node.winChoiceNumber && !isEditable && (
                        <Typography variant="overline">
                          {t('winChoiceNUmber', {
                            number: node.winChoiceNumber,
                          })}
                        </Typography>
                      )}
                    </Typography>
                  </TableRowCell>
                  <TableRowCell
                    width={100}
                    preSaleId={node.preSale.id}
                    userId={node.user.id}
                    isEditable={isEditable}
                  >
                    <Stack spacing={2}>
                      {node.choiceItemDetails.map((_, index) => {
                        if (isEditable) {
                          return (
                            <Box key={index}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    disabled={
                                      node.status === '50_invalid' ||
                                      node.status === '60_canceled'
                                    }
                                    size="small"
                                    checked={
                                      !!selectedWinners?.includes(
                                        `${node.id}:${index + 1}`
                                      )
                                    }
                                    onClick={() => {
                                      if (onSelectedWinner) {
                                        onSelectedWinner(
                                          `${node.id}:${index + 1}`,
                                          node.id
                                        )
                                      }
                                    }}
                                  />
                                }
                                sx={{ p: 0 }}
                                label={
                                  <Typography variant="body2">
                                    {t('choiceText', { number: index + 1 })}
                                  </Typography>
                                }
                              />
                            </Box>
                          )
                        }
                        return (
                          <Typography key={index} variant="body2">
                            {t('choiceText', { number: index + 1 })}
                          </Typography>
                        )
                      })}
                    </Stack>
                  </TableRowCell>
                  <TableRowCell
                    width={150}
                    preSaleId={node.preSale.id}
                    userId={node.user.id}
                    isEditable={isEditable}
                  >
                    <Stack spacing={2}>
                      {node.choiceItemDetails.map((choiceItemDetail, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{ width: 150, ...styleSets.lineCamp(1) }}
                        >
                          {choiceItemDetail.item?.title}
                        </Typography>
                      ))}
                    </Stack>
                  </TableRowCell>
                  <TableRowCell
                    width={150}
                    preSaleId={node.preSale.id}
                    userId={node.user.id}
                    isEditable={isEditable}
                  >
                    <Stack spacing={2}>
                      {node.choiceItemDetails.map((choiceItemDetail, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{
                            height: 20,
                            width: 150,
                            ...styleSets.lineCamp(1),
                          }}
                        >
                          {choiceItemDetail.skuUnit?.unitName}
                        </Typography>
                      ))}
                    </Stack>
                  </TableRowCell>
                  <TableRowCell
                    width={80}
                    preSaleId={node.preSale.id}
                    userId={node.user.id}
                    isEditable={isEditable}
                  >
                    <Stack spacing={2}>
                      {node.choiceItemDetails.map((choiceItemDetail, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{ width: 80, ...styleSets.lineCamp(1) }}
                        >
                          {choiceItemDetail.count}
                        </Typography>
                      ))}
                    </Stack>
                  </TableRowCell>
                  <TableRowCell
                    width={130}
                    preSaleId={node.preSale.id}
                    userId={node.user.id}
                    isEditable={isEditable}
                  >
                    <Typography
                      variant="body2"
                      sx={{ width: 130, ...styleSets.lineCamp(1) }}
                    >
                      {t(node.paymentMethod)}
                    </Typography>
                  </TableRowCell>
                  <TableRowCell
                    width={150}
                    preSaleId={node.preSale.id}
                    userId={node.user.id}
                    isEditable={isEditable}
                  >
                    <Typography
                      variant="body2"
                      sx={{ width: 150, ...styleSets.lineCamp(1) }}
                    >
                      {node.formattedAppliedAt}
                    </Typography>
                  </TableRowCell>
                </Fragment>
              ))}
            />
          </Table>
        </TableContainer>
      </CellFrame>
    )
  }

type TableRowCellProps = PropsWithChildren<{
  width: number
  preSaleId: string
  userId: string
  isEditable?: boolean
}>
const TableRowCell: Cell<TableRowCellProps> = ({
  width,
  preSaleId,
  userId,
  isEditable,
  children,
}) => {
  const { router, generatePath, routerPages } = useRouter()
  const Component = isEditable ? TableCell : ButtonBase

  return (
    <Component
      //@ts-ignore
      component={isEditable ? undefined : TableCell}
      padding="none"
      disableRipple
      onClick={() =>
        !isEditable &&
        router.push(
          generatePath(routerPages.ConsoleLotteryManagementDetail, {
            preSaleId,
            userId,
          })
        )
      }
      sx={{
        display: 'table-cell',
        py: 4,
        pl: 0,
        pr: 1,
        width,
        borderBottomWidth: 1,
        borderBottomColor: 'grey.100',
        borderBottomStyle: 'solid',
      }}
    >
      <Stack flex={1} sx={{ minWidth: 0, width: '100%' }}>
        {children}
      </Stack>
    </Component>
  )
}

type LotteryStatusTagProps = {
  status: string
}
const LotteryStatusTag: Cell<LotteryStatusTagProps> = ({ status }) => {
  const { t } = useTranslation('consoleLotteryManagements')

  const { text, bgcolor, color } = useMemo(() => {
    switch (status) {
      case '30_waiting_lottery':
        return {
          text: t(status),
          color: 'text.primary',
          bgcolor: 'secondary.light',
        }
      case '40_lose':
        return { text: t(status), color: 'white', bgcolor: 'grey.300' }
      case '10_win':
      case '11_win_paid':
        return { text: t(status), color: 'white', bgcolor: 'secondary.dark' }
      default:
        return { text: t(status), color: '', bgcolor: '' }
    }
  }, [status, t])

  return (
    <CellFrame>
      <Chip
        label={
          <Typography variant="overline" color={color}>
            {text}
          </Typography>
        }
        sx={{
          bgcolor,
          borderRadius: '2px',
          width: 'unset',
          height: 20,
          '.MuiChip-label': { px: 1 },
        }}
      />
    </CellFrame>
  )
}
